import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, SearchX } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { exportCsv } from "@/shared/lib/exportCsv";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ConsumptionBar, CONSUMPTION_OPTIONS, consumptionText, reachesConsumption } from "../components/ConsumptionBar";
import { FilterCard } from "../components/FilterCard";
import { FormField, selectClass } from "../components/FormField";
import { SortableTableHead } from "../components/SortableTableHead";
import { TablePagination } from "../components/TablePagination";
import { TableToolbar } from "../components/TableToolbar";
import { useInsurerConsumption } from "../hooks/useInsurerConsumption";

type Filters = { company: string; packageName: string; consumption: string };

const NO_FILTERS: Filters = { company: "", packageName: "", consumption: "" };

function distinct(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b, "fr"));
}

/** Compte Assurance : l'agent principal de chaque famille et la consommation cumulée de la famille. */
export function EnterpriseBeneficiariesPage() {
  const { t } = useTranslation();
  const pageTitle = t.safecheck.nav.beneficiaryList;
  const { families, loading, error } = useInsurerConsumption();

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  const filterFields: Array<{ key: keyof Filters; label: string; all: string; options: Array<{ value: string; label: string }> }> = [
    { key: "company", label: "Entreprise", all: "Toutes les entreprises", options: distinct(families.map((f) => f.company)).map((v) => ({ value: v, label: v })) },
    { key: "packageName", label: "Package", all: "Tous les packages", options: distinct(families.map((f) => f.package)).map((v) => ({ value: v, label: v })) },
    { key: "consumption", label: "Consommation", all: "Toutes", options: CONSUMPTION_OPTIONS },
  ];

  const sorted = useMemo(() => {
    const term = search.toLowerCase();
    const filtered = families.filter((family) => {
      if (filters.company && family.company !== filters.company) return false;
      if (filters.packageName && family.package !== filters.packageName) return false;
      if (!reachesConsumption(family.percent, filters.consumption)) return false;
      return !term || `${family.name} ${family.matricule} ${family.company}`.toLowerCase().includes(term);
    });
    return sortByColumn(filtered, sortKey, sortDirection, (family, key) => {
      if (key === "name") return family.name;
      if (key === "company") return family.company;
      if (key === "package") return family.package ?? "";
      if (key === "members") return family.members_count;
      if (key === "consumption") return family.percent ?? -1;
      return "";
    });
  }, [families, search, filters, sortKey, sortDirection]);

  const start = sorted.length === 0 ? 0 : (Math.min(page, Math.max(1, Math.ceil(sorted.length / pageSize))) - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handleExport = () =>
    exportCsv(
      "familles.csv",
      ["Agent principal", "Matricule", "Entreprise", "Package", "Membres", "Consommation de la famille"],
      sorted.map((family) => [
        family.name,
        family.matricule,
        family.company,
        family.package ?? "",
        family.members_count,
        consumptionText(family.consumed, family.ceiling, family.percent),
      ]),
    );

  const columnDefs = [
    { key: "name", label: "Agent principal", sortable: true },
    { key: "company", label: "Entreprise", sortable: true },
    { key: "package", label: "Package", sortable: true },
    { key: "members", label: "Membres", sortable: true },
    { key: "consumption", label: "Consommation de la famille", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ] as const;

  return (
    <>
      <Breadcrumbs homeHref="/dashboard/enterprise" items={[{ label: t.safecheck.nav.employeeManagement }, { label: pageTitle }]} />

      {error ? <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

      <FilterCard
        title="Filtres"
        onFilter={() => {
          setFilters(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(NO_FILTERS);
          setFilters(NO_FILTERS);
          setPage(1);
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {filterFields.map((field) => (
            <FormField key={field.key} label={field.label}>
              <select
                value={draft[field.key]}
                onChange={(event) => {
                  const value = event.target.value;
                  setDraft((current) => ({ ...current, [field.key]: value }));
                }}
                className={selectClass}
              >
                <option value="">{field.all}</option>
                {field.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>
          ))}
        </div>
      </FilterCard>

      <div className="theme-card overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">{loading ? "Chargement des familles…" : `${families.length.toLocaleString("fr-FR")} famille(s)`}</p>
        </div>

        <TableToolbar
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Rechercher un agent, un matricule, une entreprise…"
          resultCount={sorted.length}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
          onExport={handleExport}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={(key) => {
                      toggleSort(key);
                      setPage(1);
                    }}
                    sortable={column.sortable}
                    plain
                    className={column.key === "name" ? "pl-5" : undefined}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((family) => (
                <tr key={family.employee_id} className="border-b border-border last:border-0 hover:bg-accent/10">
                  <td className="py-3 pl-5 pr-4">
                    <Link to={`/dashboard/beneficiaries/family/${family.employee_id}`} className="block text-sm font-semibold text-foreground hover:text-[#4d7c0f] dark:hover:text-accent">
                      {family.name}
                    </Link>
                    <span className="mt-0.5 inline-flex rounded-md bg-violet-100 px-1.5 py-px text-[11px] font-semibold tabular-nums text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                      {family.matricule}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{family.company}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{family.package ?? <span className="text-xs text-muted">Sans package</span>}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-foreground">{family.members_count}</td>
                  <td className="px-4 py-3">
                    <ConsumptionBar consumed={family.consumed} ceiling={family.ceiling} percent={family.percent} />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/beneficiaries/family/${family.employee_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent hover:bg-accent/20"
                    >
                      <Eye size={14} aria-hidden="true" />
                      Voir
                    </Link>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={columnDefs.length} className="px-4 py-14 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-card-muted text-muted" aria-hidden="true">
                        <SearchX size={22} />
                      </span>
                      <p className="text-sm font-medium text-foreground">{loading ? "Chargement…" : "Aucune famille trouvée."}</p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={page}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={setPage}
          labels={t.safecheck.employees.enterpriseList.pagination}
        />
      </div>
    </>
  );
}
